import { memo } from "react";
import { RENDER_MODE } from "~/renderers/three-renderer";
import {
  type PlayerUpgradeKey,
  PLAYER_UPGRADES,
  useGameStore,
} from "~/state/game-store";
import type { PlayerUpgradeValues } from "~/state/game-types";
import { Button } from "~/ui/system/button";
import { Dialog } from "~/ui/system/dialog";
import { MetalWrapper } from "~/ui/system/metal-wrapper";
import { mapIterable, objectKeys } from "~/utils/objects";

function UpgradeControl({ upgradeKey }: { upgradeKey: PlayerUpgradeKey }) {
  const upgrade = PLAYER_UPGRADES[upgradeKey];
  const value = useGameStore((s) => s.playerUpgrades[upgradeKey]);
  const updatePlayerStats = useGameStore((s) => s.updatePlayerStats);
  const updatePlayerUpgrade = useGameStore((s) => s.updatePlayerUpgrade);
  const handleUpgradeChange = (
    newValue: PlayerUpgradeValues[PlayerUpgradeKey],
  ) => {
    updatePlayerUpgrade(upgradeKey, newValue);

    if (upgradeKey === "fuelCapacity") {
      const maxFuel = useGameStore.getState().playerUpgrades.fuelCapacity;
      updatePlayerStats({ fuel: maxFuel });
    }
  };

  return (
    <div className="text-left">
      <label className="block text-sm font-mono text-gray-300 mb-1">
        {upgrade.label}
      </label>

      {"options" in upgrade ? (
        <div className="joined-radii">
          {upgrade.options.map((option) => (
            <Button
              key={option}
              onClick={() => handleUpgradeChange(option)}
              aria-selected={value === option}
            >
              {option}
            </Button>
          ))}
        </div>
      ) : (
        <div className="flex gap-2 items-center">
          <input
            type="range"
            min={upgrade.min}
            max={upgrade.max}
            step={upgrade.step}
            value={value}
            onChange={(e) => handleUpgradeChange(e.target.valueAsNumber)}
            className="flex-1 h-2 bg-gray-700 rounded-lg appearance-none cursor-pointer slider"
          />
          <input
            type="number"
            min={upgrade.min}
            max={upgrade.max}
            step={upgrade.step}
            value={value}
            onChange={(e) => handleUpgradeChange(e.target.valueAsNumber)}
            className="w-20 px-2 py-1 bg-gray-800 border border-gray-600 rounded text-white text-sm font-mono"
          />
        </div>
      )}
    </div>
  );
}

export const SettingsPanel = memo(function SettingsPanel({
  backTo,
}: {
  backTo: "pause" | null;
}) {
  const setOpenMenu = useGameStore((s) => s.setOpenMenu);
  const settings = useGameStore((s) => s.settings);
  const updateSettings = useGameStore((s) => s.updateSettings);
  const updatePlayerStats = useGameStore((s) => s.updatePlayerStats);

  const handleBack = () => {
    setOpenMenu(backTo);
  };

  const handleRenderModeChange = (mode: RENDER_MODE) => {
    updateSettings({ renderMode: mode });
  };

  const handleRefillFuel = () => {
    const maxFuel = useGameStore.getState().playerUpgrades.fuelCapacity;
    updatePlayerStats({ fuel: maxFuel });
  };

  return (
    <Dialog onClose={handleBack}>
      <MetalWrapper className="w-96 max-h-[80vh] overflow-y-auto">
        <div className="text-center">
          <h2 className="text-2xl font-bold mb-6 font-mono text-white">
            Settings
          </h2>

          {/* Render Mode Setting */}
          <div className="mb-6">
            <h3 className="text-lg font-bold mb-3 font-mono text-white">
              Render Mode
            </h3>
            <div className="joined-radii flex [&>*]:flex-1">
              {(
                [
                  [RENDER_MODE.FLAT, "2D"],
                  [RENDER_MODE.ORTHO, "3D"],
                ] as const
              ).map(([value, label]) => (
                <Button
                  key={value}
                  aria-selected={settings.renderMode === value}
                  onClick={() => handleRenderModeChange(value)}
                >
                  {label}
                </Button>
              ))}
            </div>
          </div>

          {/* Admin Controls */}
          {settings.isAdmin && (
            <div className="mb-6">
              <h3 className="text-lg font-bold mb-3 font-mono text-white">
                Admin Controls
              </h3>
              <div className="space-y-4">
                {mapIterable(objectKeys(PLAYER_UPGRADES), (upgradeKey) => {
                  return (
                    <UpgradeControl key={upgradeKey} upgradeKey={upgradeKey} />
                  );
                })}
              </div>

              {/* Admin Actions */}
              <div className="mt-6 space-y-2">
                <Button
                  variant="secondary"
                  onClick={handleRefillFuel}
                  className="w-full text-sm"
                >
                  Refill Fuel
                </Button>
              </div>
            </div>
          )}

          {/* Admin Toggle (for development) */}
          <div className="mb-6">
            <Button
              variant="secondary"
              onClick={() => updateSettings({ isAdmin: !settings.isAdmin })}
              className="w-full text-sm"
            >
              {settings.isAdmin ? "Disable" : "Enable"} Admin Mode
            </Button>
          </div>

          <Button onClick={handleBack} className="w-full">
            {backTo === "pause" ? "Back to Pause Menu" : "Back"}
          </Button>
        </div>
      </MetalWrapper>
    </Dialog>
  );
});
